import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { posthog } from '../lib/analytics/posthogClient';
import { buildEventsFeedQuery, romeDay, type Event, type EventListResponse } from '@buzzmap/shared';
import { useUserPreferences } from '../hooks/useUserPreferences';
import { useEmbeddedClaim } from '../hooks/useEmbeddedClaim';
import { takeSsrFeed } from '../utils/ssrBootstrap';
import { useDiscoveryOptional, buildDiscoveryRequestHeaders } from './discoveryStore';
import { sameIdSet } from '../utils/discoveryFilters';

export interface EventsFilters {
  city?: string;
  categories: string[];
  customDateRange?: { from: string; to: string };
  search?: string;
  bbox?: { minLat: number; maxLat: number; minLng: number; maxLng: number };
  centers?: Array<{ lat: number; lng: number }>;
  radiusKm?: number;
  page: number;
  pageSize: number;
}

export interface EventsContextType {
  events: Event[];
  loading: boolean;
  error: string | null;
  total: number;
  filters: EventsFilters;
  setCity: (city: string) => void;
  toggleCategory: (category: string) => void;
  setCategories: (categories: string[]) => void;
  setCenters: (centers: Array<{ lat: number; lng: number }>) => void;
  setRadiusKm: (km: number) => void;
  clearCenters: () => void;
  setCustomDateRange: (from: string, to: string) => void;
  clearCustomDateRange: () => void;
  setSearch: (query: string) => void;
  setBbox: (bbox: EventsFilters['bbox']) => void;
  clearFilters: () => void;
  loadNextPage: () => void;
  refetch: () => void;
}

const EventsContext = createContext<EventsContextType | undefined>(undefined);

interface EventsProviderProps {
  children: ReactNode;
  initialFilterOverrides?: Partial<EventsFilters>;
  /**
   * When false, the provider still supplies the full context (filters state,
   * setters) but never fires the `/api/events` fetch. Used on the organizer
   * host: the console's filter UI (SearchBar, CategoryChipRow, useFilters)
   * reads this same context, but the console has no public event feed to
   * load and must never make a public data request. Defaults to true.
   */
  enabled?: boolean;
}

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 30;

/**
 * Legacy full-page-load bootstrap (W3, T010 review; kept for the LEGACY
 * window after T012). The old App-level `initialFilters` (removed in the
 * first T010 pass) was the only place that hydrated `?category`/`?city`/
 * `?search`/`?startDate` on a full page load of `/` or `/eventi/*`. Removing
 * it changed pre-activation behaviour for those URLs, so the identical logic
 * is re-hydrated HERE (centralized in the state owner, not a second App-level
 * snapshot) and only when the caller does not pass explicit overrides. In
 * `v2` mode the discovery descriptor owns filters (the sync effect below,
 * T012-FE) and simply overwrites whatever this initializer seeded during the
 * brief pre-gate `loading` window — the compatible/legacy window stays
 * byte-identical to HEAD.
 */
function readUrlFilterOverrides(): Partial<EventsFilters> | undefined {
  if (typeof window === 'undefined') return undefined;
  const params = new URLSearchParams(window.location.search);
  const category = params.get('category');
  const city = params.get('city');
  const search = params.get('search');
  const startDate = params.get('startDate');
  const endDate = params.get('endDate');

  if (!category && !city && !search && !startDate) return undefined;

  const overrides: Partial<EventsFilters> = {};
  if (category) overrides.categories = [category];
  if (city) overrides.city = city;
  if (search) overrides.search = search;
  if (startDate) overrides.customDateRange = { from: startDate, to: endDate || startDate };
  return overrides;
}

/** Overlay explicit overrides on the location-derived base filters (page/pageSize fall back). */
function mergeFilterOverrides(
  base: EventsFilters,
  overrides: Partial<EventsFilters>
): EventsFilters {
  return {
    ...base,
    ...overrides,
    categories: overrides.categories ?? base.categories,
    page: overrides.page ?? base.page,
    pageSize: overrides.pageSize ?? base.pageSize,
  };
}

export function EventsProvider({
  children,
  initialFilterOverrides,
  enabled = true,
}: EventsProviderProps) {
  // Active location + no-nationwide-interim-fetch gate. In v2 discovery mode
  // these come from the resolved territory (URL authority); in legacy mode from
  // the session override — see `useUserPreferences` (T010-FE).
  const { effectiveLocation, locationResolutionPending } = useUserPreferences();
  // W6 (T010 review): `/api/events` is a discovery list endpoint gated by the
  // same epoch contract as `/api/territory`. Once capabilities are known, every
  // request declares the protocol/registry metadata (or the activated server
  // answers 426). Kept in a ref so a capabilities change does NOT re-run the
  // fetch effect (which is keyed on filters/pending, not on caps identity).
  const discovery = useDiscoveryOptional();
  const capabilities = discovery?.capabilities ?? null;
  const discoveryHeadersRef = useRef<Record<string, string>>({});
  discoveryHeadersRef.current = buildDiscoveryRequestHeaders(capabilities);
  // T016-FE (W-D): the epoch gate's mode, read by the fetch effect to hold or
  // stop incompatible fetches — the rationale lives at each branch. Absent
  // provider (organizer host, isolated tests) = null → fetch as today.
  const discoveryMode = discovery?.mode ?? null;

  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState<EventsFilters>(() => {
    const loc = effectiveLocation;
    let base: EventsFilters;
    if (loc.type === 'cities') {
      base = {
        city: undefined,
        categories: [],
        centers: loc.cities.map((c) => ({ lat: c.lat, lng: c.lng })),
        radiusKm: loc.radiusKm,
        customDateRange: undefined,
        search: undefined,
        bbox: undefined,
        page: DEFAULT_PAGE,
        pageSize: DEFAULT_PAGE_SIZE,
      };
    } else if (loc.type === 'gps') {
      base = {
        city: undefined,
        categories: [],
        centers: [{ lat: loc.coords[0], lng: loc.coords[1] }],
        radiusKm: loc.radiusKm,
        customDateRange: undefined,
        search: undefined,
        bbox: undefined,
        page: DEFAULT_PAGE,
        pageSize: DEFAULT_PAGE_SIZE,
      };
    } else {
      // type === 'none': global fetch (no filter)
      base = {
        city: undefined,
        categories: [],
        centers: undefined,
        radiusKm: undefined,
        customDateRange: undefined,
        search: undefined,
        bbox: undefined,
        page: DEFAULT_PAGE,
        pageSize: DEFAULT_PAGE_SIZE,
      };
    }
    if (initialFilterOverrides) {
      return mergeFilterOverrides(base, initialFilterOverrides);
    }
    // W3: when no explicit overrides are supplied, re-hydrate the legacy
    // full-page-load URL params (the old App bootstrap) so the compatible
    // window behaves exactly as today. T012 replaces this in v2.
    const urlOverrides = readUrlFilterOverrides();
    if (urlOverrides) {
      return mergeFilterOverrides(base, urlOverrides);
    }
    return base;
  });

  const embeddedFeed = useEmbeddedClaim<EventListResponse>();

  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const refetch = useCallback(() => setRefetchTrigger((prev) => prev + 1), []);

  // T018: filter_applied PostHog event, debounced 500ms. Detects changes per
  // tracked field via a ref of the last-captured snapshot. Initial mount is
  // skipped: the ref is seeded with current filters before any user change.
  const lastFilterSnapshotRef = useRef<{
    city: string | undefined;
    categories: string[];
    customDateRange: { from: string; to: string } | undefined;
    search: string | undefined;
    radiusKm: number | undefined;
  }>({
    city: filters.city,
    categories: filters.categories,
    customDateRange: filters.customDateRange,
    search: filters.search,
    radiusKm: filters.radiusKm,
  });
  const filterCaptureTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (filterCaptureTimerRef.current) clearTimeout(filterCaptureTimerRef.current);
    filterCaptureTimerRef.current = setTimeout(() => {
      const prev = lastFilterSnapshotRef.current;
      const changes: Array<{ filter_type: string; value: string }> = [];
      if (prev.city !== filters.city) {
        changes.push({ filter_type: 'city', value: String(filters.city ?? '') });
      }
      if (
        prev.categories.length !== filters.categories.length ||
        prev.categories.some((c, i) => c !== filters.categories[i])
      ) {
        changes.push({ filter_type: 'category', value: JSON.stringify(filters.categories) });
      }
      if (JSON.stringify(prev.customDateRange) !== JSON.stringify(filters.customDateRange)) {
        changes.push({
          filter_type: 'date_range',
          value: filters.customDateRange ? JSON.stringify(filters.customDateRange) : '',
        });
      }
      if (prev.search !== filters.search) {
        changes.push({ filter_type: 'search', value: String(filters.search ?? '') });
      }
      if (prev.radiusKm !== filters.radiusKm) {
        changes.push({ filter_type: 'distance', value: String(filters.radiusKm ?? '') });
      }
      for (const ch of changes) {
        posthog.capture('filter_applied', ch);
      }
      lastFilterSnapshotRef.current = {
        city: filters.city,
        categories: filters.categories,
        customDateRange: filters.customDateRange,
        search: filters.search,
        radiusKm: filters.radiusKm,
      };
    }, 500);
    return () => {
      if (filterCaptureTimerRef.current) clearTimeout(filterCaptureTimerRef.current);
    };
  }, [filters.city, filters.categories, filters.customDateRange, filters.search, filters.radiusKm]);

  // Sync effectiveLocation -> filters.centers / radiusKm / bbox.
  // Guarded by a stringified-key ref so preferences object recreation (with unchanged
  // location content) does NOT trigger a redundant setFilters / refetch.
  //
  // This keeps `filters.centers`/`filters.radiusKm` available for anything
  // that reads them from the context, but the fetch effect below does NOT
  // rely on this sync having landed — see the comment there. Relying on it
  // used to fire one throwaway nationwide fetch every time `effectiveLocation`
  // changed, most visibly on a territory route, where `filters` (this
  // component's own state) can still be one render behind a just-applied
  // `effectiveLocation` (T014-FE double-fetch fix).
  const locKeyRef = useRef<string>(JSON.stringify(effectiveLocation));
  useEffect(() => {
    const loc = effectiveLocation;
    const key = JSON.stringify(loc);
    if (key === locKeyRef.current) return;
    locKeyRef.current = key;

    if (loc.type === 'cities') {
      setFilters((prev) => ({
        ...prev,
        centers: loc.cities.map((c) => ({ lat: c.lat, lng: c.lng })),
        radiusKm: loc.radiusKm,
        bbox: undefined,
        page: 1,
      }));
    } else if (loc.type === 'gps') {
      setFilters((prev) => ({
        ...prev,
        centers: [{ lat: loc.coords[0], lng: loc.coords[1] }],
        radiusKm: loc.radiusKm,
        bbox: undefined,
        page: 1,
      }));
    } else {
      // type === 'none'
      setFilters((prev) => ({
        ...prev,
        centers: undefined,
        radiusKm: undefined,
        page: 1,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveLocation]);

  // T012-FE: in v2 the discovery descriptor (the normalized URL) is the sole
  // authority for the query-shape filters. Geography (centers/radius) already
  // flows through `effectiveLocation` (T010-FE); categories, dates and search
  // hydrate HERE, so a refresh, a browser back/forward hop or a query-only
  // navigation refetches exactly what the URL describes — including the
  // invalid case, which never reaches this effect because an invalid
  // normalization leaves the descriptor null and the feed held (W-B).
  // The raw `setFilters` (not a public setter) and a change-guard keep this
  // sync from ever looping through navigation or refetching an identical
  // query. Legacy/organizer keep the mount-once bootstraps above — the
  // compatible window stays HEAD-equivalent.
  const descriptor = discovery?.mode === 'v2' && enabled ? discovery.descriptor : null;
  // Il periodo risolto e le date esplicite si applicano insieme, come gli
  // AND overlap di SSR/API. I due limiti restano indipendenti: end-only non
  // inventa un inizio. La richiesta usa i valori del render corrente, non
  // una copia customDateRange che può non essere ancora sincronizzata.
  const period =
    discovery?.mode === 'v2' && enabled ? (discovery.resolution?.period ?? null) : null;
  let v2StartDate = descriptor?.filters.startDate;
  let v2EndDate = descriptor?.filters.endDate;
  if (period) {
    if (!v2StartDate || v2StartDate < period.from) v2StartDate = period.from;
    if (!v2EndDate || v2EndDate > period.to) v2EndDate = period.to;
  }
  const dateBoundsKey = JSON.stringify([v2StartDate ?? null, v2EndDate ?? null]);
  const dateBoundsKeyRef = useRef(dateBoundsKey);
  useEffect(() => {
    if (!descriptor) return;
    const datesChanged = dateBoundsKeyRef.current !== dateBoundsKey;
    dateBoundsKeyRef.current = dateBoundsKey;
    const nextCategories = descriptor.categoryIds;
    const nextRange = v2StartDate && v2EndDate ? { from: v2StartDate, to: v2EndDate } : undefined;
    const nextSearch = descriptor.filters.search;
    setFilters((prev) => {
      const sameRange =
        prev.customDateRange?.from === nextRange?.from &&
        prev.customDateRange?.to === nextRange?.to;
      // Change-guard: an identical query shape keeps `prev` (no refetch, no
      // loop). S-3: `city` must be undefined — v2 has no city filter, so a
      // legacy `?city=` seeded during the pre-gate `loading` pass never
      // survives into the activated epoch.
      const unchanged =
        !datesChanged &&
        sameIdSet(prev.categories, nextCategories) &&
        sameRange &&
        (prev.search ?? undefined) === (nextSearch ?? undefined) &&
        prev.city === undefined;
      if (unchanged) return prev;
      return {
        ...prev,
        categories: nextCategories,
        customDateRange: nextRange,
        search: nextSearch,
        city: undefined,
        page: 1,
      };
    });
  }, [descriptor, dateBoundsKey, v2StartDate, v2EndDate]);

  useEffect(() => {
    // organizer host: no public event feed to load — never fire /api/events.
    if (!enabled) {
      setLoading(false);
      return;
    }

    // T016-FE: stop incompatible fetches — the upgrade/maintenance state owns
    // the session (plan l.71/74), and every discovery request would be
    // rejected 426 anyway. The blocking overlay covers the screen; the feed
    // neither fetches nor spins.
    if (discoveryMode === 'upgrade-required') {
      setLoading(false);
      return;
    }

    // T016-FE (W-D): hold the feed while the epoch gate is still negotiating.
    // A headerless cold-start request would 426 at the activated epoch and
    // flash a generic error before the capabilities bootstrap lands; holding
    // means the FIRST fetch already carries the negotiated headers.
    // Pre-activation cost: the first fetch waits out the capabilities RTT
    // (documented pre-activation delta — the SSR shell covers the gap, same
    // class as T010-W4's legacy-resolution hold).
    if (discoveryMode === 'loading') {
      setLoading(true);
      return;
    }

    // Territory route whose /api/territory resolution hasn't landed yet
    // (T014-FE double-fetch fix, see UserContext.locationResolutionPending):
    // hold the fetch rather than firing a nationwide query that gets
    // discarded the instant the resolved location applies. Stay in the
    // loading state so the UI doesn't flash empty results in the meantime.
    if (locationResolutionPending) {
      setLoading(true);
      return;
    }

    // Centers/radius for the query, derived straight from `effectiveLocation`
    // rather than read off `filters.centers`/`filters.radiusKm`. Those are a
    // copy synced by the effect above and can still be one render behind a
    // just-applied `effectiveLocation` (e.g. right after a territory
    // resolution both applies the location and clears
    // `locationResolutionPending` — see T014-FE) — reading the copy here
    // could fire once with the still-stale (typically nationwide) value.
    // Reading the source of truth directly means this effect is correct on
    // whichever render it happens to run. Falls back to `filters.centers`
    // only when `effectiveLocation` itself carries no location (type
    // 'none') — the one case an explicit `setCenters` call (organizer host
    // only, where this effect never reaches this point: mounted with
    // `enabled=false`) would still need to be honored.
    const locationCenters =
      effectiveLocation.type === 'cities'
        ? effectiveLocation.cities.map((c) => ({ lat: c.lat, lng: c.lng }))
        : effectiveLocation.type === 'gps'
          ? [{ lat: effectiveLocation.coords[0], lng: effectiveLocation.coords[1] }]
          : undefined;
    const locationRadiusKm =
      effectiveLocation.type === 'cities' || effectiveLocation.type === 'gps'
        ? effectiveLocation.radiusKm
        : undefined;
    const queryCenters = locationCenters ?? filters.centers;
    const queryRadiusKm = locationRadiusKm ?? filters.radiusKm;

    const abortController = new AbortController();

    const fetchEvents = async () => {
      setLoading(true);
      setError(null);

      try {
        // La query è costruita da `@buzzmap/shared`: lo stesso codice con cui il
        // server registra la richiesta del primo feed incorporato nel documento
        // (T204-INT), quindi "stessa richiesta" si decide in un punto solo.
        // Legacy: l'intervallo personalizzato conta solo con entrambi i limiti.
        const legacyRange =
          filters.customDateRange?.from && filters.customDateRange?.to
            ? filters.customDateRange
            : undefined;
        const query = buildEventsFeedQuery({
          centers: queryCenters,
          radiusKm: queryRadiusKm,
          city: filters.city,
          bbox: filters.bbox,
          categories: filters.categories,
          startDate: descriptor ? v2StartDate : legacyRange?.from,
          endDate: descriptor ? v2EndDate : legacyRange?.to,
          search: filters.search,
          page: filters.page,
          pageSize: filters.pageSize,
        });

        // Applica una pagina di risultati (risposta di rete o incorporata).
        const applyPage = (data: Partial<EventListResponse>) => {
          if (filters.page === 1) {
            setEvents(data.events || []);
          } else {
            setEvents((prev) => [...prev, ...(data.events || [])]);
          }
          setTotal(data.total || 0);

          // How many events the user actually SAW for the filters in effect.
          // `filter_applied` records the intent; this records the outcome, which
          // is what separates "not interested" from "nothing was there to show".
          // First-page fetches only: paginated appends are not a new result set,
          // and the initial default fetch is deliberately included — it is the
          // first impression a visitor gets.
          if (filters.page === 1) {
            const total = Number(data.total) || 0;
            posthog.capture('results_shown', {
              total,
              is_empty: total === 0,
              categories: JSON.stringify(filters.categories),
              category_count: filters.categories.length,
              radius_km: queryRadiusKm ?? null,
              center_count: queryCenters?.length ?? 0,
              city: filters.city ?? null,
              date_from: filters.customDateRange?.from ?? null,
              date_to: filters.customDateRange?.to ?? null,
              has_search: Boolean(filters.search),
              has_bbox: Boolean(filters.bbox),
            });
          }
        };

        // T204-INT: la prima pagina del feed può arrivare nel documento SSR.
        // Solo pagina 1, solo se la query coincide carattere per carattere con
        // quella registrata e il documento è del giorno di Roma corrente (la
        // query non contiene la data); una volta sola, per rinfrescare e
        // paginare resta la rete. La chiave include `refetchTrigger`, così un
        // refresh esplicito non rilegge mai i dati vecchi.
        if (filters.page === 1) {
          const embedded = embeddedFeed.claim(`${refetchTrigger}|${query}`, () =>
            takeSsrFeed(query, romeDay(new Date()))
          );
          if (embedded) {
            applyPage(embedded);
            return;
          }
        }
        embeddedFeed.release();

        const response = await fetch(`/api/events?${query}`, {
          signal: abortController.signal,
          // W6: declare the discovery epoch metadata once known (empty object
          // pre-bootstrap → the pre-activation gate is a pass-through).
          headers: discoveryHeadersRef.current,
        });
        if (response.status === 426) {
          // T016-FE: the epoch moved under this tab (stale registry
          // version/protocol header). Renegotiate through the discovery
          // provider instead of surfacing a raw HTTP error, and keep an
          // honest translated message until the gate settles — the gate
          // then refetches with fresh headers or takes over with the
          // blocking upgrade state.
          discovery?.reportUpgradeRequired();
          throw new Error('Aggiornamento in corso: riprova tra qualche istante');
        }
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (!abortController.signal.aborted) {
          applyPage(data);
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        if (!abortController.signal.aborted) {
          setError(err instanceof Error ? err.message : 'Failed to fetch events');
          console.error('Error fetching events:', err);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchEvents();

    return () => {
      abortController.abort();
    };
    // `effectiveLocation` deliberately excluded: it's read directly above
    // (see that comment) for correctness on whatever render this effect
    // happens to run, but it always changes together with `filters` (via
    // the sync effect) or `locationResolutionPending` (via HomePage's
    // territory resolution) — either already re-triggers this effect, so
    // adding `effectiveLocation` too would only add a second, redundant
    // fetch with an identical query.
    // `discoveryMode` IS a dependency (T016-FE W-D): the loading→legacy/v2
    // transition is exactly when a held cold-start fetch must fire.
    // Le date v2 primitive rilevano end-only senza rifare la lista per tracking o pan.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters,
    refetchTrigger,
    enabled,
    locationResolutionPending,
    discoveryMode,
    v2StartDate,
    v2EndDate,
  ]);

  // setCity updates the session-scoped city filter only (e.g. via URL params on EsploraPage).
  // It does NOT modify user preferences (effectiveLocation's underlying source remains untouched).
  const setCity = useCallback((city: string) => {
    setFilters((prev) => ({ ...prev, city, bbox: undefined, page: 1 }));
  }, []);

  const toggleCategory = useCallback((category: string) => {
    setFilters((prev) => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter((c) => c !== category)
        : [...prev.categories, category],
      page: 1,
    }));
  }, []);

  const setCategories = useCallback((categories: string[]) => {
    setFilters((prev) => ({ ...prev, categories, page: 1 }));
  }, []);

  const setCenters = useCallback((centers: Array<{ lat: number; lng: number }>) => {
    setFilters((prev) => ({ ...prev, centers, bbox: undefined, page: 1 }));
  }, []);

  const setRadiusKm = useCallback((km: number) => {
    setFilters((prev) => ({ ...prev, radiusKm: km, page: 1 }));
  }, []);

  const clearCenters = useCallback(() => {
    setFilters((prev) => ({ ...prev, centers: undefined, radiusKm: undefined, page: 1 }));
  }, []);

  const setCustomDateRange = useCallback((from: string, to: string) => {
    setFilters((prev) => ({
      ...prev,
      customDateRange: { from, to },
      page: 1,
    }));
  }, []);

  const clearCustomDateRange = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      customDateRange: undefined,
      page: 1,
    }));
  }, []);

  const setSearch = useCallback((query: string) => {
    setFilters((prev) => ({
      ...prev,
      search: query || undefined,
      page: 1,
    }));
  }, []);

  const setBbox = useCallback((bbox: EventsFilters['bbox']) => {
    setFilters((prev) => ({ ...prev, bbox, page: 1 }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters((prev) => ({
      city: prev.city,
      categories: [],
      customDateRange: undefined,
      search: undefined,
      bbox: undefined,
      centers: undefined,
      radiusKm: undefined,
      page: 1,
      pageSize: prev.pageSize,
    }));
  }, []);

  const loadNextPage = useCallback(() => {
    setFilters((prev) => ({ ...prev, page: prev.page + 1 }));
  }, []);

  const value = useMemo<EventsContextType>(
    () => ({
      events,
      loading,
      error,
      total,
      filters,
      setCity,
      toggleCategory,
      setCategories,
      setCenters,
      setRadiusKm,
      clearCenters,
      setCustomDateRange,
      clearCustomDateRange,
      setSearch,
      setBbox,
      clearFilters,
      loadNextPage,
      refetch,
    }),
    [
      events,
      loading,
      error,
      total,
      filters,
      setCity,
      toggleCategory,
      setCategories,
      setCenters,
      setRadiusKm,
      clearCenters,
      setCustomDateRange,
      clearCustomDateRange,
      setSearch,
      setBbox,
      clearFilters,
      loadNextPage,
      refetch,
    ]
  );

  return <EventsContext.Provider value={value}>{children}</EventsContext.Provider>;
}

export function useEventsContext(): EventsContextType {
  const context = useContext(EventsContext);
  if (context === undefined) {
    throw new Error('useEventsContext must be used within an EventsProvider');
  }
  return context;
}

/**
 * Null when no `EventsProvider` is mounted — the same escape hatch
 * `useDiscoveryOptional` gives surfaces that can render without a provider
 * (organizer host, isolated unit tests). Consumers that only PUSH results
 * into the feed (e.g. `useTerritoryRouteResolution` applying a resolved
 * period/category) use this: there is nothing to write to when the provider
 * is absent, and throwing would make the hook unusable as a probe.
 */
export function useEventsContextOptional(): EventsContextType | null {
  return useContext(EventsContext) ?? null;
}
